import { CustomError } from "@/types/errors/CustomError";
import { ErrorManager } from "@/types/errors/ErrorManager";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import ExclamationIcon from "@mui/icons-material/PriorityHigh";
import { Alert, AlertColor, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar } from "@mui/material";
import { observer } from "mobx-react";
import { useState } from "react";

export interface IErrorListProps {
  open: boolean;
}

export const ErrorList = observer((props: IErrorListProps) => {
  //console.log('ErrorList');
  const { open } = props;
  const appStore = useAppStore();

  const handleClose = () => {
    appStore.errorManager.setOpenErrors(false);
  };

  if (appStore.errorManager.errorStack.errors.length === 0) {
    return <></>;
  }

  const error: CustomError = appStore.errorManager.errorStack.errors.slice(-1)[0];

  // Treat canceled/aborted operations as non-errors: don't show toast/snackbar for these.
  try {
    const text = [
      error?.title,
      error?.message,
      (error as any)?.cause?.message,
      (error as any)?.cause?.response?.data?.InnerMessage?.message,
      (error as any)?.cause?.response?.data?.innerMessage,
    ]
      .filter(Boolean)
      .join(" ");
    if (/(^|[^a-z])(abort|aborted|cancel|canceled)([^a-z]|$)/i.test(text)) {
      // Remove this canceled error from the stack and render nothing
      appStore.errorManager.errorStack.clear(appStore.errorManager.errorStack.errors.length - 1);
      return <></>;
    }
  } catch {
    // ignore guard failures and proceed to render
  }
  const severity = (error.severity ?? "error") as AlertColor;
  console.error("error: ", error);

  return (
    <>
      <Snackbar
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={15000}
        sx={{
          "&.MuiSnackbar-root": {
            top: 64,
            right: 0,
          },
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%", maxWidth: "600px", alignItems: "center" }} icon={<ExclamationIcon fontSize="inherit" />}>
          <Box>{error.title}</Box>
          <Box>{error.message}</Box>
          <Box>{error.cause?.response?.data?.InnerMessage?.message}</Box>
        </Alert>
      </Snackbar>
      <ErrorButton errorManager={appStore.errorManager} />
    </>
  );
});

interface IErrorButtonProps {
  errorManager: ErrorManager;
}

const ErrorButton = observer((props: IErrorButtonProps) => {
  //console.log('ErrorButton');
  const { errorManager } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Button color={"warning"} onClick={() => setOpen(true)}>
          Error!
        </Button>
      </Box>
      {open ? (
        <ErrorDialog
          errorManager={errorManager}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
});

interface IErrorDialogProps {
  errorManager: ErrorManager;
  onClose: () => void;
}

const ErrorDialog = observer((props: IErrorDialogProps) => {
  //console.log('ErrorDialog');
  const { errorManager, onClose } = props;

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={true}
        onClose={onClose}
        PaperProps={{
          sx: {
            height: "80%",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("error-list.title")}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {errorManager.errorStack.errors.map((error: CustomError, index: number) => (
            <ErrorBar
              key={index}
              error={error}
              onClose={() => {
                errorManager.errorStack.clear(index);
              }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => errorManager.errorStack.clear()}>{i18n.t("error-list.clear-button.title")}</Button>
          <Button onClick={onClose}>{i18n.t("error-list.close-button.title")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

interface IErrorBarProps {
  error: CustomError;
  onClose: () => void;
}

const ErrorBar = observer((props: IErrorBarProps) => {
  //console.log('ErrorBar');
  const { error, onClose } = props;

  return (
    <Alert
      severity={"error"}
      onClose={() => {
        onClose();
      }}
    >
      <pre>
        {[error.title, error.message].filter((val) => val).join(": ")}
        {error.cause?.response?.data?.innerMessage}
      </pre>
    </Alert>
  );
});
